import { useNavigate } from 'react-router-dom';
import { SignupLayout } from './Layout.tsx';
import { useSignup } from './state.ts';

/**
 * Screen 5 — avatar grid (skippable).
 *
 * MVP: 6 emoji-glyph placeholders so the screen is functional + reviewable.
 * Real avatar art ships with Sprint 5 content authoring (separate from the
 * code path); the slug stored here ('placeholder-rocket' etc.) maps cleanly
 * to whatever real asset replaces it later.
 */
const AVATARS = [
  { slug: 'placeholder-rocket', emoji: '🚀', label: 'Rocket' },
  { slug: 'placeholder-cat', emoji: '🐱', label: 'Cat' },
  { slug: 'placeholder-dragon', emoji: '🐲', label: 'Dragon' },
  { slug: 'placeholder-robot', emoji: '🤖', label: 'Robot' },
  { slug: 'placeholder-star', emoji: '⭐', label: 'Star' },
  { slug: 'placeholder-pizza', emoji: '🍕', label: 'Pizza' },
];

export function Step5Avatar(): JSX.Element {
  const navigate = useNavigate();
  const { avatarSlug, setAvatar } = useSignup();

  const onContinue = (): void => navigate('/signup/6');
  const onSkip = (): void => {
    setAvatar(null);
    navigate('/signup/6');
  };

  return (
    <SignupLayout
      step={5}
      title="Pick a picture (optional)"
      subtitle="Choose an avatar for your kid, or skip and pick one later."
    >
      <div className="grid grid-cols-3 gap-3">
        {AVATARS.map((a) => (
          <button
            key={a.slug}
            type="button"
            aria-label={a.label}
            onClick={() => setAvatar(a.slug)}
            className={`flex aspect-square items-center justify-center rounded-2xl border-2 text-5xl transition active:scale-95 ${
              avatarSlug === a.slug
                ? 'border-amber-500 bg-amber-50'
                : 'border-slate-200 bg-white hover:border-amber-300'
            }`}
          >
            <span aria-hidden="true">{a.emoji}</span>
          </button>
        ))}
      </div>

      <div className="mt-6 flex flex-col gap-3">
        <button
          type="button"
          onClick={onContinue}
          disabled={!avatarSlug}
          className="rounded-2xl bg-amber-500 px-6 py-4 text-base font-semibold text-white shadow transition active:scale-95 hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Continue
        </button>
        <button
          type="button"
          onClick={onSkip}
          className="rounded-2xl border border-slate-200 bg-white px-6 py-4 text-base text-slate-600 transition active:scale-95 hover:bg-slate-50"
        >
          Skip for now
        </button>
      </div>
    </SignupLayout>
  );
}
